/** @jsx jsx */

import { useContext, useEffect, useRef } from 'react';
import { css, jsx } from '@emotion/core';
import { motion } from 'framer-motion';
import { CarouselContext } from './Carousel';
import isAppleComputerInc from 'services/isAppleComputerInc';
import { ImageZoomContext } from 'components/lib/Image/ImageZoom';

const defaultBulletFormatting = {
  ul: {
    listStyleType: 'disc',
    ul: {
      listStyleType: 'circle',
      ul: {
        listStyleType: 'square',
      },
    },
  },
  ol: {
    ul: {
      listStyleType: 'circle',
      ul: {
        listStyleType: 'square',
      },
    },
  },
  '& .chameleon-text > div': {
    'ul, ol': {
      listStylePosition: 'inside',
    },
    '> ul, > ol': {
      display: 'inline-block',
      marginLeft: '0px',
    },
  },
};

const CarouselSlide = ({
  index,
  currentIndex,
  totalSlides,
  imgHeight,
  carouselTextColor,
  animation,
  slide,
  includeText,
}) => {
  const img = slide.props.children[0];
  const text = slide.props.children[1];

  const { hasAnimated } = useContext(CarouselContext)!;

  const slideRef = useRef<HTMLLIElement | null>(null);
  const ar = {};

  const slideChanged = useRef(currentIndex);

  if (
    // currentIndex !== 0 &&
    // currentIndex === index
    slideChanged.current !== currentIndex
  ) {
    ar['role'] = 'alert';
    // console.log('slideChanged should update from :', slideChanged.current);
    slideChanged.current = currentIndex;
    // console.log('to :', slideChanged.current);
  }

  let content = text?.props?.content;
  if (content === '<p data-type="paragraph"></p>') {
    content = false;
  }

  const imageZoomContextValue = {
    overideStyles: {
      right: 5,
      bottom: 25,
    },
  };

  useEffect(() => {
    if (!slideRef.current) {
      return;
    }

    // do we chack for selectableElems in the slide
    // and if any. give the slide focus ?

    const selectableElems = Array.from(
      slideRef.current.querySelectorAll('a, button')
    ) as HTMLElement[];

    // inert holds up !!!
    if (currentIndex !== index) {
      slideRef.current.setAttribute('inert', '');
    } else {
      slideRef.current.removeAttribute('inert');
    }
  }, [currentIndex]);

  const slideTextStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: currentIndex === index ? 'auto' : 0,
    padding: '13px 7px',
    overflow: 'hidden',
    wordBreak: 'break-word',
    textAlign: 'center',
    transition: 'height 0.2s',
    ...defaultBulletFormatting,
    ...(window.dev() && currentIndex === index ? { padding: '3px 5px' } : {}),
    ...(currentIndex === index ? { transition: 'height 0.2s' } : {}),
    ...(carouselTextColor
      ? {
          '& .chameleon-text *:not(span[data-type="color"])': {
            color: carouselTextColor,
          },
          '& .chameleon-text span[data-type="color"] *': {
            color: 'inherit',
          },
        }
      : {}),
  } as any;

  // isiOS()
  const applesToApples = isAppleComputerInc();

  return applesToApples ? (
    <li
      ref={slideRef}
      className={currentIndex === index ? 'fader_-2s' : ''}
      css={css`
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        width: 100%;
        list-style-type: none;
        opacity: ${currentIndex === index ? 1 : 0};
        transition: opacity 0.35s;
        transform: translateX(-${100 * currentIndex}%);
      `}
      {...ar}
      aria-hidden={currentIndex !== index}
    >
      <div
        css={css`
          height: ${imgHeight}px;
          .chameleon-image,
          .chameleon-image img {
            width: 100%;
            height: ${imgHeight}px !important;
          }
          .circle-img img {
            height: 0 !important;
          }
        `}
      >
        <ImageZoomContext.Provider value={imageZoomContextValue}>
          {img}
        </ImageZoomContext.Provider>
      </div>

      {includeText && (
        <div key={`slide-text-${currentIndex}`} css={css(slideTextStyles)}>
          {text}
        </div>
      )}
    </li>
  ) : (
    // @ts-ignore
    <motion.li
      ref={slideRef}
      css={css`
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
        width: 100%;
        list-style-type: none;
        .circle-img img {
          height: 0 !important;
        }
      `}
      animate={{
        transform: `translateX(-${100 * currentIndex}%)`,
        opacity: currentIndex === index ? 1 : 0,
      }}
      transition={
        {
          natural: {
            duration: 0.35,
            type: 'spring',
            stiffness: 260 / 1.5,
            damping: 19,
          },
          smooth: {
            duration: 0.35,
          },
          none: {
            duration: 0,
          },
        }[animation]
      }
      onAnimationComplete={() => {
        if (!hasAnimated) {
          if (index === 0) {
          }
          return;
        }

        if (window.dev && !window.dev() && slideRef.current) {
          if (currentIndex !== index) {
            return;
          }
        }
      }}
      {...ar}
      aria-hidden={currentIndex !== index}
    >
      <div
        css={css`
          height: ${imgHeight}px;
          .chameleon-image,
          .chameleon-image img {
            width: 100%;
            height: ${imgHeight}px !important;
          }
        `}
      >
        <ImageZoomContext.Provider value={imageZoomContextValue}>
          {img}
        </ImageZoomContext.Provider>
      </div>

      <p className="sr-only">
        Slide {index + 1} of {totalSlides}
      </p>
      {includeText && <div css={css(slideTextStyles)}>{text}</div>}
    </motion.li>
  );
};

export default CarouselSlide;
